import { useMemo } from 'react';
import { usePortfolioSpeciesSource } from 'src/app/portfolio/state/portfolioSpeciesSource';
import { useSiteSpeciesRiskAssessmentQuery, useSiteSpeciesRiskQuery } from '../state/api/portfolioSiteExplorerGraphSlice';
import type { SiteSpeciesAssessmentItem, SpeciesRiskAssessmentItem } from '../state/api/porfolioSiteExplorerQuery';

/** One species at one site, as the modal renders it, whichever source supplied it. */
export type SiteSpeciesRiskItem = {
    scientificName: string;
    commonName: string;
    iucnRedListCategory: string;
    predictionLevel: string;
    hasEDNAData: boolean;
    hasUploadedData: boolean;
    foundInEDNA: boolean;
    foundInUploadedData: boolean;
};

export type SiteSpeciesRiskResult = {
    siteName: string;
    country?: string;
    area?: number;
    redListSpecies: SiteSpeciesRiskItem[];
    invasiveSpecies: SiteSpeciesRiskItem[];
    isFetching: boolean;
};

type Args = {
    siteId: string;
    filters?: { name: string; values: string[] }[];
};

/**
 * The species risk modal's data, from whichever source the surrounding route declared.
 *
 * The two shapes differ — the projection nests the detection facts in an
 * `assessments` array and calls the site `projectName`; the grid returns them flat
 * under `siteName` — so normalising happens here rather than in the modal. The modal
 * then renders one shape and never learns which source it came from.
 */
export const useSiteSpeciesRisk = ({ siteId, filters }: Args): SiteSpeciesRiskResult => {
    const isGrid = usePortfolioSpeciesSource() === 'grid';
    const args = { siteId, ...(filters?.length ? { filters } : {}) };

    // Both called so the Rules of Hooks hold; only the active one is unskipped.
    const projection = useSiteSpeciesRiskAssessmentQuery(args, { skip: !siteId || isGrid });
    const grid = useSiteSpeciesRiskQuery(args, { skip: !siteId || !isGrid });

    // Memoised, and both branches unconditionally so the Rules of Hooks hold. The
    // arrays are rebuilt by map(), so without this every render hands the modal fresh
    // identities for unchanged data; its table-state effect keys on them and would
    // re-fire until React gives up with "Maximum update depth exceeded".
    const gridResult = useMemo(
        () => ({
            siteName: grid.currentData?.siteName ?? '',
            country: grid.currentData?.country,
            area: grid.currentData?.area,
            redListSpecies: (grid.currentData?.redListSpecies ?? []).map(fromGrid),
            invasiveSpecies: (grid.currentData?.invasiveSpecies ?? []).map(fromGrid),
            isFetching: grid.isFetching,
        }),
        [grid.currentData, grid.isFetching]
    );

    const projectionResult = useMemo(
        () => ({
            siteName: projection.currentData?.projectName ?? '',
            country: projection.currentData?.country,
            area: projection.currentData?.area,
            redListSpecies: (projection.currentData?.redListSpecies ?? []).map(fromProjection),
            invasiveSpecies: (projection.currentData?.invasiveSpecies ?? []).map(fromProjection),
            isFetching: projection.isFetching,
        }),
        [projection.currentData, projection.isFetching]
    );

    return isGrid ? gridResult : projectionResult;
};

const fromGrid = (species: SiteSpeciesAssessmentItem): SiteSpeciesRiskItem => ({
    scientificName: species.scientificName,
    commonName: species.commonName ?? '',
    iucnRedListCategory: species.iucnRedListCategory,
    predictionLevel: species.predictionLevel,
    hasEDNAData: species.hasEDNAData,
    hasUploadedData: species.hasUploadedData,
    foundInEDNA: species.foundInEDNA,
    foundInUploadedData: species.foundInUploadedData,
});

/**
 * The detection half sits in an `assessments` array here. Read defensively: a species
 * carrying no assessment row would otherwise throw and take the whole modal with it,
 * rather than render one row with nothing known about detection.
 */
const fromProjection = (species: SpeciesRiskAssessmentItem): SiteSpeciesRiskItem => {
    const assessment = species.assessments?.[0];
    return {
        scientificName: species.scientificName,
        commonName: species.commonName ?? '',
        iucnRedListCategory: species.iucnRedListCategory,
        predictionLevel: assessment?.predictionLevel ?? '',
        hasEDNAData: Boolean(assessment?.hasEDNAData),
        hasUploadedData: Boolean(assessment?.hasUploadedData),
        foundInEDNA: Boolean(assessment?.foundInEDNA),
        foundInUploadedData: Boolean(assessment?.foundInUploadedData),
    };
};
